import { useState, useMemo, useEffect, useCallback } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { useExplorationAchievement } from '@/hooks/useAchievementTriggers';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
  ArrowLeftRight,
  ArrowDownLeft,
  ArrowUpRight,
  CreditCard,
  Banknote,
  PiggyBank,
  Home,
  Car,
  DollarSign,
  Briefcase,
  Building2,
  Landmark,
  Gem,
  Pencil,
  Wallet,
  Users,
  MapPin,
  Calendar,
  Ruler,
  Percent,
  Watch,
  Coins,
  Bitcoin,
  BarChart3,
  Globe,
  Activity,
  X,
  Upload,
  FileText,
  Trash2,
  Loader2,
  Eye,
  ExternalLink,
  Target,
  ChevronDown,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useCurrency } from '@/contexts';
import { useAccountTransactions } from '@/hooks/useAccountTransactions';
import { useAccounts } from '@/hooks/useAccounts';
import { useAccountGoalAllocations } from '@/hooks/useAccountGoalAllocations';
import { useBalanceChart } from '@/hooks/useBalanceChart';
import { useTransactions } from '@/hooks/useTransactions';
import { useTransactionFilters } from '@/hooks/useTransactionFilters';
import { GroupedTransactionList } from '@/components/TransactionItem';
import { TransactionModal } from '@/components/TransactionModal';
import { TransferModal } from '@/components/TransferModal';
import { findBankByName, getBankLogoUrl } from '@/lib/banks';
import { AccountSourceBadge } from '@/components/AccountSourceBadge';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  getCryptoPrice,
  getCryptoChart,
  type CryptoPrice,
  type CryptoChartPoint,
} from '@/lib/crypto';
import {
  getStockData,
  type StockQuote,
  type StockChartPoint,
  type StockRange,
} from '@/lib/stock-price';

import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

const SUBTYPE_ICONS: Record<string, typeof DollarSign> = {
  checking: Banknote,
  savings: PiggyBank,
  cash: DollarSign,
  investment: TrendingUp,
  real_estate: Home,
  vehicle: Car,
  other_asset: Gem,
  watch: Watch,
  jewelry: Gem,
  precious_metal: Coins,
  crypto: Bitcoin,
  pea_stock: BarChart3,
  credit_card: CreditCard,
  mortgage: Building2,
  loan: Landmark,
  other_liability: Briefcase,
  joint_account: Users,
  money_market: Landmark,
};

const SUBTYPE_COLORS: Record<string, string> = {
  checking: 'from-blue-500 to-cyan-500',
  savings: 'from-green-500 to-emerald-500',
  cash: 'from-emerald-400 to-green-500',
  investment: 'from-purple-500 to-pink-500',
  real_estate: 'from-teal-500 to-green-500',
  vehicle: 'from-indigo-500 to-blue-500',
  other_asset: 'from-yellow-500 to-amber-400',
  watch: 'from-purple-500 to-violet-500',
  jewelry: 'from-purple-400 to-pink-500',
  precious_metal: 'from-yellow-500 to-amber-500',
  crypto: 'from-orange-500 to-orange-600',
  pea_stock: 'from-blue-500 to-blue-600',
  credit_card: 'from-red-500 to-pink-500',
  mortgage: 'from-rose-500 to-red-500',
  loan: 'from-rose-500 to-red-500',
  other_liability: 'from-gray-500 to-slate-500',
  joint_account: 'from-blue-500 to-indigo-500',
  money_market: 'from-teal-500 to-cyan-500',
};

export default function AccountDetailsPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  useExplorationAchievement('explore_account_detail');
  const { id: accountId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const confirm = useConfirm();
  const { formatAmountInCurrency, formatCompact } = useCurrency();
  const { accounts, loadAccounts, updateAccount } = useAccounts();
  const { user } = useAuth();
  const { toast } = useToast();
  const { allocations, totalAllocated } = useAccountGoalAllocations(accountId);
  const { transactions, loading, loadTransactions } = useAccountTransactions(accountId);
  const { transactions: allTransactions, deleteTransaction } = useTransactions();
  const {
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    clearDateRange,
    filteredTransactions,
  } = useTransactionFilters(transactions);

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | undefined>();

  // Crypto live data
  const [cryptoPrice, setCryptoPrice] = useState<CryptoPrice | null>(null);
  const [cryptoChart, setCryptoChart] = useState<CryptoChartPoint[]>([]);
  const [chartDays, setChartDays] = useState<number>(7);
  const [chartLoading, setChartLoading] = useState(false);

  // PEA stock live data
  const [stockQuote, setStockQuote] = useState<StockQuote | null>(null);
  const [stockChart, setStockChart] = useState<StockChartPoint[]>([]);
  const [stockRange, setStockRange] = useState<StockRange>('1mo');
  const [stockChartLoading, setStockChartLoading] = useState(false);

  // RIB document
  const [ribUploading, setRibUploading] = useState(false);
  const [ribDeleting, setRibDeleting] = useState(false);
  const [ribViewerOpen, setRibViewerOpen] = useState(false);
  const [goalsExpanded, setGoalsExpanded] = useState(false);
  const [ribBlobUrl, setRibBlobUrl] = useState<string | null>(null);

  // Balance chart period
  const [balanceChartPeriod, setBalanceChartPeriod] = useState<string>('1m');
  const BALANCE_PERIOD_DAYS: Record<string, number> = {
    '1m': 30,
    '3m': 90,
    '6m': 180,
    '1y': 365,
    all: 730,
  };

  const account = useMemo(() => {
    return accounts.find((a) => a.id === accountId);
  }, [accounts, accountId]);

  const acctCurrency = account?.currency || 'EUR';
  const formatAccountAmount = useCallback(
    (amount: number, options?: Parameters<typeof formatAmountInCurrency>[2]) =>
      formatAmountInCurrency(amount, acctCurrency, options),
    [formatAmountInCurrency, acctCurrency]
  );

  const ribUrl = useMemo(() => {
    if (!account?.metadata) return null;
    return (account.metadata as Record<string, unknown>)?.rib_document_path as string | null;
  }, [account]);

  const handleRibUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user || !accountId) return;
    e.target.value = '';

    if (file.type !== 'application/pdf') {
      toast({
        title: t('accountDetails.rib.invalidType' as any),
        description: t('accountDetails.rib.invalidTypeDesc' as any),
        variant: 'destructive',
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t('accountDetails.rib.fileTooLarge' as any),
        description: t('accountDetails.rib.fileTooLargeDesc' as any),
        variant: 'destructive',
      });
      return;
    }

    try {
      setRibUploading(true);

      // Delete previous file if exists
      if (ribUrl) {
        await supabase.storage.from('account-documents').remove([ribUrl]);
      }

      const filePath = `${user.id}/${accountId}/rib-${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('account-documents')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Store path in account metadata
      const currentMeta = (account?.metadata as Record<string, unknown>) || {};
      await updateAccount(accountId, {
        metadata: {
          ...currentMeta,
          rib_document_path: filePath,
          rib_updated_at: new Date().toISOString(),
        },
      });

      toast({
        title: t('accountDetails.rib.uploaded' as any),
        description: t('accountDetails.rib.uploadedDesc' as any),
      });
    } catch {
      toast({
        title: t('accountDetails.rib.uploadError' as any),
        description: t('accountDetails.rib.uploadErrorDesc' as any),
        variant: 'destructive',
      });
    } finally {
      setRibUploading(false);
    }
  };

  const handleRibView = async () => {
    if (!ribUrl) return;
    const { data, error } = await supabase.storage.from('account-documents').download(ribUrl);
    if (error || !data) return;
    const url = URL.createObjectURL(data);
    setRibBlobUrl(url);
    setRibViewerOpen(true);
  };

  const handleRibDownload = () => {
    if (!ribBlobUrl) return;
    const a = document.createElement('a');
    a.href = ribBlobUrl;
    a.download = `RIB-${account?.name || 'document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const closeRibViewer = () => {
    setRibViewerOpen(false);
    if (ribBlobUrl) {
      URL.revokeObjectURL(ribBlobUrl);
      setRibBlobUrl(null);
    }
  };

  const handleRibDelete = async () => {
    if (!ribUrl || !accountId) return;
    const ok = await confirm({
      description: t('accountDetails.rib.deleteConfirm' as any),
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      setRibDeleting(true);
      await supabase.storage.from('account-documents').remove([ribUrl]);
      const currentMeta = (account?.metadata as Record<string, unknown>) || {};
      const { rib_document_path, rib_updated_at, ...rest } = currentMeta;
      await updateAccount(accountId, { metadata: rest });
      toast({
        title: t('accountDetails.rib.deleted' as any),
        description: t('accountDetails.rib.deletedDesc' as any),
      });
    } catch {
      toast({
        title: t('accountDetails.rib.uploadError' as any),
        description: t('accountDetails.rib.uploadErrorDesc' as any),
        variant: 'destructive',
      });
    } finally {
      setRibDeleting(false);
    }
  };

  const cryptoCoinId =
    account?.subtype === 'crypto'
      ? ((account.metadata as Record<string, unknown>)?.coin_id as string | undefined)
      : undefined;

  // Fetch live crypto price
  useEffect(() => {
    if (!cryptoCoinId) return;
    let cancelled = false;
    getCryptoPrice(cryptoCoinId).then((p) => {
      if (!cancelled) setCryptoPrice(p);
    });
    const interval = setInterval(() => {
      getCryptoPrice(cryptoCoinId).then((p) => {
        if (!cancelled) setCryptoPrice(p);
      });
    }, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [cryptoCoinId]);

  // Fetch chart data
  const loadChart = useCallback(
    async (days: number) => {
      if (!cryptoCoinId) return;
      setChartLoading(true);
      const currency = language === 'fr' ? 'eur' : 'eur'; // always EUR for now
      const data = await getCryptoChart(cryptoCoinId, currency, days);
      setCryptoChart(data);
      setChartLoading(false);
    },
    [cryptoCoinId, language]
  );

  useEffect(() => {
    loadChart(chartDays);
  }, [loadChart, chartDays]);

  // PEA stock symbol from metadata
  const stockSymbol =
    account?.subtype === 'pea_stock'
      ? ((account.metadata as Record<string, unknown>)?.symbol as string | undefined)
      : undefined;

  // Fetch PEA stock data (quote + chart)
  const loadStockData = useCallback(
    async (range: StockRange) => {
      if (!stockSymbol) return;
      setStockChartLoading(true);
      const result = await getStockData(stockSymbol, range);
      if (result) {
        setStockQuote(result.quote);
        setStockChart(result.chart);
      }
      setStockChartLoading(false);
    },
    [stockSymbol]
  );

  useEffect(() => {
    loadStockData(stockRange);
  }, [loadStockData, stockRange]);

  // Refresh stock quote periodically
  useEffect(() => {
    if (!stockSymbol) return;
    const interval = setInterval(() => {
      loadStockData(stockRange);
    }, 5 * 60_000);
    return () => clearInterval(interval);
  }, [stockSymbol, stockRange, loadStockData]);

  useEffect(() => {
    if (account?.subtype === 'cash') {
      navigate('/accounts', { replace: true });
    }
  }, [account, navigate]);

  // Period-filtered stats for KPIs (matching date range filter, or balance chart period as fallback)
  const periodStats = useMemo(() => {
    if (!accountId)
      return {
        totalIncome: 0,
        totalExpense: 0,
        totalTransfersIn: 0,
        totalTransfersOut: 0,
        netFlow: 0,
      };

    let filtered = transactions;
    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter((t) => {
        const d = new Date(t.date);
        if (dateRange.startDate && d < dateRange.startDate) return false;
        if (dateRange.endDate && d > dateRange.endDate) return false;
        return true;
      });
    } else {
      const days = BALANCE_PERIOD_DAYS[balanceChartPeriod] || 30;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      filtered = filtered.filter((t) => new Date(t.date) >= cutoff);
    }

    let totalIncome = 0,
      totalExpense = 0,
      totalTransfersIn = 0,
      totalTransfersOut = 0;
    filtered.forEach((t) => {
      if (t.type === 'income' && t.account_id === accountId) totalIncome += t.amount;
      else if (t.type === 'expense' && t.account_id === accountId) totalExpense += t.amount;
      else if (t.type === 'transfer') {
        if (t.account_id === accountId) totalTransfersOut += t.amount;
        if (t.to_account_id === accountId) totalTransfersIn += t.amount;
      }
    });
    return {
      totalIncome,
      totalExpense,
      totalTransfersIn,
      totalTransfersOut,
      netFlow: totalIncome + totalTransfersIn - totalExpense - totalTransfersOut,
    };
  }, [transactions, accountId, balanceChartPeriod, dateRange]);

  // Monthly variation for balance display
  const monthlyVariation = useMemo(() => {
    if (!accountId) return null;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const filtered = transactions.filter((t) => new Date(t.date) >= cutoff);
    let net = 0;
    filtered.forEach((t) => {
      if (t.type === 'income' && t.account_id === accountId) net += t.amount;
      else if (t.type === 'expense' && t.account_id === accountId) net -= t.amount;
      else if (t.type === 'transfer') {
        if (t.account_id === accountId) net -= t.amount;
        if (t.to_account_id === accountId) net += t.amount;
      }
    });
    return net;
  }, [transactions, accountId]);

  const { data: balanceChartData } = useBalanceChart(
    accounts,
    BALANCE_PERIOD_DAYS[balanceChartPeriod] || 30,
    accountId
  );

  const Icon = account?.subtype ? SUBTYPE_ICONS[account.subtype] || Wallet : Wallet;
  const gradient = account?.subtype
    ? SUBTYPE_COLORS[account.subtype] || 'from-gray-500 to-slate-500'
    : 'from-gray-500 to-slate-500';
  const matchedBank = findBankByName(account?.institution);

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      description: t('transactions.delete.confirm'),
      variant: 'destructive',
    });
    if (!ok) return;
    const result = await deleteTransaction(id);
    if (result.success) {
      await loadTransactions();
      await loadAccounts();
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsTransactionModalOpen(true);
  };

  const handleModalClose = () => {
    setIsTransactionModalOpen(false);
    setSelectedTransaction(undefined);
  };

  const handleModalSuccess = async () => {
    await loadTransactions();
    await loadAccounts();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const isFr = language === 'fr';
    const prefix = isFr ? 'il y a ' : '';
    const suffix = isFr ? '' : ' ago';

    if (diffHours < 24) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 1) return t('accounts.justNow');
      if (diffMinutes < 60) return `${prefix}${diffMinutes} min${suffix}`;
      return `${prefix}${Math.floor(diffHours)}h${suffix}`;
    }

    return date.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!account) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">{t('accountDetails.notFound')}</p>
          <button
            onClick={() => navigate('/accounts')}
            className="mt-4 text-[#16c47f] hover:underline"
          >
            {t('accountDetails.backToAccounts')}
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="accounts" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-700/50">
          <div className="p-6">
            {/* Back button */}
            <button
              onClick={() => navigate('/accounts')}
              className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('accountDetails.backToAccounts')}
            </button>

            {/* Account Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {matchedBank ? (
                  <div className="w-14 h-14 rounded-2xl bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-700/50 flex items-center justify-center shadow-lg overflow-hidden">
                    <img
                      src={getBankLogoUrl(matchedBank)}
                      alt={matchedBank.name}
                      className="w-9 h-9 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : account.subtype === 'crypto' &&
                  (account.metadata as Record<string, unknown>)?.thumb ? (
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg overflow-hidden`}
                  >
                    <img
                      src={(account.metadata as Record<string, unknown>).thumb as string}
                      alt={account.name}
                      className="w-9 h-9 rounded-full"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {account.name}
                    </h1>
                    <AccountSourceBadge source={account.source} showManual />
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t(`accounts.subtype.${account.subtype}` as any)}
                    </span>
                    {account.institution && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {account.institution}
                        </span>
                      </>
                    )}
                    {account.interest_rate != null && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {account.subtype === 'credit_card'
                            ? `${t('addBank.apr' as any)}: ${account.interest_rate}%`
                            : `${t('addBank.interestRate' as any)}: ${account.interest_rate}%`}
                        </span>
                      </>
                    )}
                    {account.credit_limit != null && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('addBank.creditLimit' as any)}:{' '}
                          {formatAccountAmount(account.credit_limit)}
                        </span>
                      </>
                    )}
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {t('accounts.lastUpdated')}: {formatDate(account.updated_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p
                    className={`text-3xl font-bold tabular-nums ${account.type === 'liability' ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}
                  >
                    {account.type === 'liability' ? '-' : ''}
                    {formatAccountAmount(account.balance)}
                  </p>
                  {monthlyVariation !== null && monthlyVariation !== 0 && (
                    <p
                      className={`text-xs font-medium mt-0.5 ${monthlyVariation > 0 ? 'text-green-500' : 'text-[#78909C]'}`}
                    >
                      {monthlyVariation > 0 ? '+' : ''}
                      {formatAccountAmount(monthlyVariation)}{' '}
                      {language === 'fr' ? 'ce mois' : 'this month'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate(`/accounts/${accountId}/edit`)}
                  className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <Pencil className="w-5 h-5 text-gray-400 hover:text-[#16c47f]" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Goal Allocations Block - Collapsible */}
          {allocations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50">
              <button
                onClick={() => setGoalsExpanded(!goalsExpanded)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#16c47f]" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-none">
                    {t('accountDetails.goals.title' as any)}
                  </h3>
                  <span className="text-sm font-medium text-amber-600 dark:text-amber-400 leading-none">
                    {formatAccountAmount(totalAllocated)}
                  </span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${goalsExpanded ? 'rotate-180' : ''}`}
                />
              </button>

              {goalsExpanded && (
                <div className="px-6 pb-6 space-y-5">
                  {/* Summary: Real / Allocated / Available */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                        {t('accountDetails.goals.realBalance' as any)}
                      </p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                        {formatAccountAmount(account.balance)}
                      </p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 text-center">
                      <p className="text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">
                        {t('accountDetails.goals.allocated' as any)}
                      </p>
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400 tabular-nums">
                        {formatAccountAmount(totalAllocated)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 text-center">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">
                        {t('accountDetails.goals.available' as any)}
                      </p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                        {formatAccountAmount(Math.max(0, account.balance - totalAllocated))}
                      </p>
                    </div>
                  </div>

                  {/* Explanation text */}
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                    {t('accountDetails.goals.explanation' as any)}
                  </p>

                  {/* Goal list */}
                  <div className="space-y-3">
                    {allocations.map((alloc) => (
                      <div
                        key={alloc.goal_id}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl"
                      >
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#16c47f] to-emerald-400 flex items-center justify-center flex-shrink-0">
                          <Target className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {alloc.goal_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatAccountAmount(alloc.allocated)}{' '}
                            {t('accountDetails.goals.allocatedFrom' as any)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Real Estate Details */}
          {account.subtype === 'real_estate' &&
            account.metadata &&
            (() => {
              const m = account.metadata as Record<string, string | number | null>;
              const hasDetails = m.property_type || m.surface || m.purchase_price || m.city;
              if (!hasDetails) return null;
              return (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('accountDetails.realEstate.details' as any)}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {!!m.property_type && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.propertyType' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                          <Home className="w-3.5 h-3.5 text-teal-500" />
                          {t(`addRealEstate.type.${m.property_type}` as any)}
                        </p>
                      </div>
                    )}
                    {!!m.category && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.category' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {t(`addRealEstate.cat.${m.category}` as any)}
                        </p>
                      </div>
                    )}
                    {m.city || m.country ? (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.city' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-teal-500" />
                          {[m.city, m.country].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    ) : null}
                    {!!m.purchase_price && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.purchasePrice' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatAccountAmount(Number(m.purchase_price))}
                        </p>
                      </div>
                    )}
                    {!!m.surface && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.surface' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                          <Ruler className="w-3.5 h-3.5 text-teal-500" />
                          {String(m.surface)} m²
                        </p>
                      </div>
                    )}
                    {!!m.land_surface && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.landSurface' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {String(m.land_surface)} m²
                        </p>
                      </div>
                    )}
                    {!!m.construction_year && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.constructionYear' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-teal-500" />
                          {String(m.construction_year)}
                        </p>
                      </div>
                    )}
                    {!!m.dpe && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.dpe' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {String(m.dpe)}
                        </p>
                      </div>
                    )}
                    {m.ownership_percent != null && Number(m.ownership_percent) !== 100 && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.ownershipPercent' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                          <Percent className="w-3.5 h-3.5 text-teal-500" />
                          {String(m.ownership_percent)}%
                        </p>
                      </div>
                    )}
                    {!!m.purchase_date && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {t('addRealEstate.purchaseDate' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(String(m.purchase_date)).toLocaleDateString(
                            language === 'fr' ? 'fr-FR' : 'en-US',
                            { day: 'numeric', month: 'short', year: 'numeric' }
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Fees summary */}
                  {m.agency_fees || m.notary_fees || m.renovation_fees || m.furnishing_fees ? (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                        {t('accountDetails.realEstate.fees' as any)}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {!!m.agency_fees && (
                          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('addRealEstate.agencyFees' as any)}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                              {formatAccountAmount(Number(m.agency_fees))}
                            </p>
                          </div>
                        )}
                        {!!m.notary_fees && (
                          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('addRealEstate.notaryFees' as any)}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                              {formatAccountAmount(Number(m.notary_fees))}
                            </p>
                          </div>
                        )}
                        {!!m.renovation_fees && (
                          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('addRealEstate.renovationFees' as any)}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                              {formatAccountAmount(Number(m.renovation_fees))}
                            </p>
                          </div>
                        )}
                        {!!m.furnishing_fees && (
                          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('addRealEstate.furnishingFees' as any)}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                              {formatAccountAmount(Number(m.furnishing_fees))}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Description */}
                  {!!m.description && (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                        {t('addRealEstate.description' as any)}
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {String(m.description)}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Other asset metadata */}
          {account.subtype === 'other_asset' &&
            account.metadata &&
            (() => {
              const m = account.metadata as Record<string, string | number | null>;
              return (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                    {t('accountDetails.otherAsset.details' as any)}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    {!!m.asset_category && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addOtherAsset.category' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {t(`addOtherAsset.categories.${m.asset_category}` as any)}
                        </p>
                      </div>
                    )}
                    {!!m.quantity && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addOtherAsset.quantity' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {m.quantity}
                        </p>
                      </div>
                    )}
                    {!!m.purchase_unit_price && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addOtherAsset.purchaseUnitPrice' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatAccountAmount(Number(m.purchase_unit_price))}
                        </p>
                      </div>
                    )}
                    {!!m.current_unit_price && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addOtherAsset.currentUnitPrice' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatAccountAmount(Number(m.current_unit_price))}
                        </p>
                      </div>
                    )}
                    {!!m.ownership_percent && Number(m.ownership_percent) !== 100 && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addOtherAsset.ownershipPercent' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {m.ownership_percent}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {/* Watch / Jewelry metadata */}
          {(account.subtype === 'watch' || account.subtype === 'jewelry') &&
            account.metadata &&
            (() => {
              const m = account.metadata as Record<string, string | number | boolean | null>;
              return (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                    {t('accountDetails.watchJewelry.details' as any)}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    {!!m.purchase_price && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addWatchJewelry.purchasePrice' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatAccountAmount(Number(m.purchase_price))}
                        </p>
                      </div>
                    )}
                    {!!m.condition && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('accountDetails.watchJewelry.condition' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {t(`addWatchJewelry.condition.${m.condition}` as any)}
                        </p>
                      </div>
                    )}
                    {!!m.seller && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('accountDetails.watchJewelry.seller' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {String(m.seller)}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                        {t('accountDetails.watchJewelry.originalBox' as any)}
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {m.has_original_box
                          ? t('accountDetails.watchJewelry.yes' as any)
                          : t('accountDetails.watchJewelry.no' as any)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                        {t('accountDetails.watchJewelry.papers' as any)}
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {m.has_papers
                          ? t('accountDetails.watchJewelry.yes' as any)
                          : t('accountDetails.watchJewelry.no' as any)}
                      </p>
                    </div>
                    {!!m.ownership_percent && Number(m.ownership_percent) !== 100 && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addWatchJewelry.ownershipPercent' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {m.ownership_percent}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {/* Precious Metal metadata */}
          {account.subtype === 'precious_metal' &&
            account.metadata &&
            (() => {
              const m = account.metadata as Record<string, string | number | null>;
              return (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                    {t('accountDetails.preciousMetal.details' as any)}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    {!!m.quantity && Number(m.quantity) > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addPreciousMetal.quantity' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {m.quantity}
                        </p>
                      </div>
                    )}
                    {!!m.purchase_unit_price && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addPreciousMetal.purchaseUnitPrice' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatAccountAmount(Number(m.purchase_unit_price))}
                        </p>
                      </div>
                    )}
                    {!!m.ownership_percent && Number(m.ownership_percent) !== 100 && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                          {t('addPreciousMetal.ownershipPercent' as any)}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {m.ownership_percent}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {/* Crypto Dashboard */}
          {account.subtype === 'crypto' &&
            account.metadata &&
            (() => {
              const m = account.metadata as Record<string, string | number | null>;
              const quantity = Number(m.quantity) || 0;
              const purchasePrice = Number(m.purchase_unit_price) || 0;
              const totalInvested = quantity * purchasePrice;
              const currentUnitPrice = cryptoPrice?.eur ?? 0;
              const currentValue = quantity * currentUnitPrice;
              const pnl = currentUnitPrice > 0 ? currentValue - totalInvested : 0;
              const pnlPercent =
                totalInvested > 0 && currentUnitPrice > 0
                  ? ((currentValue - totalInvested) / totalInvested) * 100
                  : 0;
              const change24h = cryptoPrice?.eur_24h_change ?? null;
              const marketCap = cryptoPrice?.eur_market_cap ?? null;
              const isPositivePnl = pnl >= 0;
              const isPositive24h = (change24h ?? 0) >= 0;
              const chartColor =
                cryptoChart.length >= 2
                  ? cryptoChart[cryptoChart.length - 1].price >= cryptoChart[0].price
                    ? '#16c47f'
                    : '#ef4444'
                  : '#16c47f';

              const chartData = cryptoChart.map((p) => ({
                date: p.timestamp,
                price: p.price,
              }));

              const formatChartDate = (ts: number) => {
                const d = new Date(ts);
                if (chartDays <= 1)
                  return d.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                if (chartDays <= 7)
                  return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    day: 'numeric',
                    month: 'short',
                  });
                return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                  day: 'numeric',
                  month: 'short',
                });
              };

              const formatChartPrice = (val: number) => formatAccountAmount(val);

              const timeframes = [
                { label: '24H', days: 1 },
                { label: '7D', days: 7 },
                { label: '30D', days: 30 },
                { label: '90D', days: 90 },
                { label: '1Y', days: 365 },
              ];

              return (
                <div className="space-y-4">
                  {/* Live Price + P&L Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Current Price */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-bl-full" />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                          <Activity className="w-4 h-4 text-orange-500" />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.crypto.livePrice' as any)}
                        </p>
                      </div>
                      {cryptoPrice ? (
                        <>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatAccountAmount(currentUnitPrice)}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            ${cryptoPrice.usd.toLocaleString('en-US', { maximumFractionDigits: 2 })}{' '}
                            USD
                          </p>
                        </>
                      ) : (
                        <div className="h-8 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>

                    {/* 24h Change */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div
                        className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${isPositive24h ? 'from-green-500/10' : 'from-red-500/10'} to-transparent rounded-bl-full`}
                      />
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className={`w-8 h-8 rounded-lg ${isPositive24h ? 'bg-green-500/10' : 'bg-red-500/10'} flex items-center justify-center`}
                        >
                          {isPositive24h ? (
                            <TrendingUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.crypto.change24h' as any)}
                        </p>
                      </div>
                      {change24h != null ? (
                        <p
                          className={`text-2xl font-bold ${isPositive24h ? 'text-green-500' : 'text-red-500'}`}
                        >
                          {isPositive24h ? '+' : ''}
                          {change24h.toFixed(2)}%
                        </p>
                      ) : (
                        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>

                    {/* P&L */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div
                        className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${isPositivePnl ? 'from-emerald-500/10' : 'from-red-500/10'} to-transparent rounded-bl-full`}
                      />
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className={`w-8 h-8 rounded-lg ${isPositivePnl ? 'bg-emerald-500/10' : 'bg-red-500/10'} flex items-center justify-center`}
                        >
                          <BarChart3
                            className={`w-4 h-4 ${isPositivePnl ? 'text-emerald-500' : 'text-red-500'}`}
                          />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.crypto.pnl' as any)}
                        </p>
                      </div>
                      {currentUnitPrice > 0 ? (
                        <>
                          <p
                            className={`text-2xl font-bold ${isPositivePnl ? 'text-emerald-500' : 'text-red-500'}`}
                          >
                            {isPositivePnl ? '+' : ''}
                            {formatAccountAmount(pnl)}
                          </p>
                          <p
                            className={`text-xs mt-1 ${isPositivePnl ? 'text-emerald-400' : 'text-red-400'}`}
                          >
                            {isPositivePnl ? '+' : ''}
                            {pnlPercent.toFixed(2)}%
                          </p>
                        </>
                      ) : (
                        <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>

                    {/* Market Cap */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full" />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Globe className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.crypto.marketCap' as any)}
                        </p>
                      </div>
                      {marketCap != null ? (
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {marketCap >= 1e12
                            ? `€${(marketCap / 1e12).toFixed(2)}T`
                            : marketCap >= 1e9
                              ? `€${(marketCap / 1e9).toFixed(1)}B`
                              : `€${(marketCap / 1e6).toFixed(0)}M`}
                        </p>
                      ) : (
                        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>
                  </div>

                  {/* Price Chart */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        {m.thumb && (
                          <img src={m.thumb as string} alt="" className="w-8 h-8 rounded-full" />
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {t('accountDetails.crypto.priceChart' as any)}
                          </h3>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {m.symbol ? String(m.symbol).toUpperCase() : ''} / EUR
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                        {timeframes.map((tf) => (
                          <button
                            key={tf.days}
                            onClick={() => setChartDays(tf.days)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              chartDays === tf.days
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            {tf.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="h-[320px] relative">
                      <AnimatePresence mode="wait">
                        {chartLoading ? (
                          <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center"
                          >
                            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          </motion.div>
                        ) : chartData.length > 0 ? (
                          <motion.div
                            key={`chart-${chartDays}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="h-full"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart
                                data={chartData}
                                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                              >
                                <defs>
                                  <linearGradient id="cryptoGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <XAxis
                                  dataKey="date"
                                  tickFormatter={formatChartDate}
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                                  dy={10}
                                  minTickGap={40}
                                />
                                <YAxis
                                  domain={['auto', 'auto']}
                                  tickFormatter={(v: number) => formatCompact(v)}
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                                  width={70}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (!active || !payload?.[0]) return null;
                                    const dateStr = new Date(label).toLocaleDateString(
                                      language === 'fr' ? 'fr-FR' : 'en-US',
                                      {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      }
                                    );
                                    return (
                                      <div
                                        style={{
                                          background: 'rgba(17, 24, 39, 0.95)',
                                          backdropFilter: 'blur(12px)',
                                          borderRadius: '14px',
                                          padding: '14px 18px',
                                          boxShadow:
                                            '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
                                          minWidth: '160px',
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: '#9ca3af',
                                            fontSize: '12px',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          {dateStr}
                                        </div>
                                        <div
                                          style={{
                                            color: '#fff',
                                            fontSize: '18px',
                                            fontWeight: 700,
                                            letterSpacing: '-0.02em',
                                          }}
                                        >
                                          {formatChartPrice(payload[0].value as number)}
                                        </div>
                                      </div>
                                    );
                                  }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="price"
                                  stroke={chartColor}
                                  strokeWidth={2.5}
                                  fill="url(#cryptoGradient)"
                                  animationDuration={800}
                                  animationEasing="ease-out"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </motion.div>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500">
                            {t('accountDetails.crypto.noChartData' as any)}
                          </div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>

                  {/* Holdings Details */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                      {t('accountDetails.crypto.details' as any)}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {!!m.symbol && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addCrypto.symbol' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {String(m.symbol).toUpperCase()}
                          </p>
                        </div>
                      )}
                      {quantity > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addCrypto.quantity' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {quantity}
                          </p>
                        </div>
                      )}
                      {purchasePrice > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addCrypto.purchasePrice' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatAccountAmount(purchasePrice)}
                          </p>
                        </div>
                      )}
                      {totalInvested > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('accountDetails.crypto.totalInvested' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatAccountAmount(totalInvested)}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })()}

          {/* PEA Stock Dashboard */}
          {account.subtype === 'pea_stock' &&
            account.metadata &&
            (() => {
              const m = account.metadata as Record<string, string | number | null>;
              const quantity = Number(m.quantity) || 0;
              const purchasePrice = Number(m.purchase_unit_price) || 0;
              const totalInvested = quantity * purchasePrice;
              const currentPrice = stockQuote?.price ?? 0;
              const currentValue = currentPrice > 0 ? quantity * currentPrice : 0;
              const pnl = currentPrice > 0 ? currentValue - totalInvested : 0;
              const pnlPercent =
                totalInvested > 0 && currentPrice > 0
                  ? ((currentValue - totalInvested) / totalInvested) * 100
                  : 0;
              const dayChange = stockQuote?.changePercent ?? null;
              const isPositivePnl = pnl >= 0;
              const isPositiveDay = (dayChange ?? 0) >= 0;
              const peaChartColor =
                stockChart.length >= 2
                  ? stockChart[stockChart.length - 1].price >= stockChart[0].price
                    ? '#16c47f'
                    : '#ef4444'
                  : '#3b82f6';

              const chartData = stockChart.map((p) => ({
                date: p.timestamp,
                price: p.price,
              }));

              const formatStockChartDate = (ts: number) => {
                const d = new Date(ts);
                if (stockRange === '1d')
                  return d.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                  day: 'numeric',
                  month: 'short',
                });
              };

              const stockTimeframes: { label: string; range: StockRange }[] = [
                { label: '1D', range: '1d' },
                { label: '5D', range: '5d' },
                { label: '1M', range: '1mo' },
                { label: '3M', range: '3mo' },
                { label: '6M', range: '6mo' },
                { label: '1Y', range: '1y' },
              ];

              return (
                <div className="space-y-4">
                  {/* Live Price + P&L Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Current Price */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full" />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Activity className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.pea.livePrice' as any)}
                        </p>
                      </div>
                      {stockQuote?.price != null ? (
                        <>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {stockQuote.price.toLocaleString(
                              language === 'fr' ? 'fr-FR' : 'en-US',
                              { style: 'currency', currency: stockQuote.currency || 'EUR' }
                            )}
                          </p>
                          {dayChange != null && (
                            <p
                              className={`text-xs mt-1 font-semibold ${isPositiveDay ? 'text-green-500' : 'text-red-500'}`}
                            >
                              {isPositiveDay ? '▲' : '▼'} {isPositiveDay ? '+' : ''}
                              {dayChange.toFixed(2)}%
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="h-8 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>

                    {/* P&L */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div
                        className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${isPositivePnl ? 'from-emerald-500/10' : 'from-red-500/10'} to-transparent rounded-bl-full`}
                      />
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className={`w-8 h-8 rounded-lg ${isPositivePnl ? 'bg-emerald-500/10' : 'bg-red-500/10'} flex items-center justify-center`}
                        >
                          <BarChart3
                            className={`w-4 h-4 ${isPositivePnl ? 'text-emerald-500' : 'text-red-500'}`}
                          />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.pea.pnl' as any)}
                        </p>
                      </div>
                      {currentPrice > 0 ? (
                        <>
                          <p
                            className={`text-2xl font-bold ${isPositivePnl ? 'text-emerald-500' : 'text-red-500'}`}
                          >
                            {isPositivePnl ? '+' : ''}
                            {formatAccountAmount(pnl)}
                          </p>
                          <p
                            className={`text-xs mt-1 ${isPositivePnl ? 'text-emerald-400' : 'text-red-400'}`}
                          >
                            {isPositivePnl ? '+' : ''}
                            {pnlPercent.toFixed(2)}%
                          </p>
                        </>
                      ) : (
                        <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>

                    {/* Current Value */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-bl-full" />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                          <Globe className="w-4 h-4 text-indigo-500" />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                          {t('accountDetails.pea.currentValue' as any)}
                        </p>
                      </div>
                      {currentPrice > 0 ? (
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {formatAccountAmount(currentValue)}
                        </p>
                      ) : (
                        <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      )}
                    </motion.div>
                  </div>

                  {/* Price Chart */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('accountDetails.pea.priceChart' as any)}
                        </h3>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {m.symbol ? String(m.symbol).toUpperCase() : ''} ·{' '}
                          {String(m.stock_type || 'stock').toUpperCase()}
                        </p>
                      </div>
                      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                        {stockTimeframes.map((tf) => (
                          <button
                            key={tf.range}
                            onClick={() => setStockRange(tf.range)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              stockRange === tf.range
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            {tf.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="h-[320px] relative">
                      <AnimatePresence mode="wait">
                        {stockChartLoading ? (
                          <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center"
                          >
                            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          </motion.div>
                        ) : chartData.length > 0 ? (
                          <motion.div
                            key={`chart-${stockRange}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="h-full"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart
                                data={chartData}
                                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                              >
                                <defs>
                                  <linearGradient id="peaGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={peaChartColor} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={peaChartColor} stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <XAxis
                                  dataKey="date"
                                  tickFormatter={formatStockChartDate}
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                                  dy={10}
                                  minTickGap={40}
                                />
                                <YAxis
                                  domain={['auto', 'auto']}
                                  tickFormatter={(v: number) => formatCompact(v)}
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                                  width={70}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (!active || !payload?.[0]) return null;
                                    const dateStr = new Date(label).toLocaleDateString(
                                      language === 'fr' ? 'fr-FR' : 'en-US',
                                      { day: 'numeric', month: 'long', year: 'numeric' }
                                    );
                                    return (
                                      <div
                                        style={{
                                          background: 'rgba(17, 24, 39, 0.95)',
                                          backdropFilter: 'blur(12px)',
                                          borderRadius: '14px',
                                          padding: '14px 18px',
                                          boxShadow:
                                            '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
                                          minWidth: '160px',
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: '#9ca3af',
                                            fontSize: '12px',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          {dateStr}
                                        </div>
                                        <div
                                          style={{
                                            color: '#fff',
                                            fontSize: '18px',
                                            fontWeight: 700,
                                            letterSpacing: '-0.02em',
                                          }}
                                        >
                                          {formatAccountAmount(payload[0].value as number)}
                                        </div>
                                      </div>
                                    );
                                  }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="price"
                                  stroke={peaChartColor}
                                  strokeWidth={2.5}
                                  fill="url(#peaGradient)"
                                  animationDuration={800}
                                  animationEasing="ease-out"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </motion.div>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500">
                            {t('accountDetails.pea.noChartData' as any)}
                          </div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>

                  {/* Holdings Details */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                      {t('accountDetails.pea.details' as any)}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {!!m.symbol && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addPea.symbol' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {String(m.symbol).toUpperCase()}
                          </p>
                        </div>
                      )}
                      {!!m.stock_type && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addPea.stockType' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white uppercase">
                            {m.stock_type}
                          </p>
                        </div>
                      )}
                      {quantity > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addPea.quantity' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {quantity}
                          </p>
                        </div>
                      )}
                      {purchasePrice > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('addPea.purchasePrice' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatAccountAmount(purchasePrice)}
                          </p>
                        </div>
                      )}
                      {totalInvested > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {t('accountDetails.pea.totalInvested' as any)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatAccountAmount(totalInvested)}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })()}

          {/* RIB Document Section - for bank accounts */}
          {(account.subtype === 'checking' ||
            account.subtype === 'savings' ||
            account.subtype === 'joint_account' ||
            account.subtype === 'money_market') && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('accountDetails.rib.title' as any)}
                    </p>
                    {ribUrl ? (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-[#16c47f]">PDF</p>
                        {typeof (account.metadata as Record<string, unknown>)?.rib_updated_at ===
                          'string' && (
                          <>
                            <span className="text-gray-300 dark:text-gray-600">•</span>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Mis à jour le{' '}
                              {formatDate(
                                (account.metadata as Record<string, unknown>)
                                  .rib_updated_at as string
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {t('accountDetails.rib.noFile' as any)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  {ribUrl ? (
                    <>
                      <button
                        onClick={handleRibView}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#16c47f]/10 hover:bg-[#16c47f]/20 text-[#16c47f] transition-colors flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        {t('accountDetails.rib.view' as any)}
                      </button>
                      <label className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors cursor-pointer">
                        {ribUploading ? (
                          <Loader2 className="w-3 h-3 animate-spin inline" />
                        ) : (
                          t('accountDetails.rib.replace' as any)
                        )}
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleRibUpload}
                          className="hidden"
                          disabled={ribUploading}
                        />
                      </label>
                      <button
                        onClick={handleRibDelete}
                        disabled={ribDeleting}
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                      >
                        {ribDeleting ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </>
                  ) : (
                    <label className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#16c47f]/10 hover:bg-[#16c47f]/20 text-[#16c47f] transition-colors cursor-pointer">
                      {ribUploading ? (
                        <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                      ) : (
                        <Upload className="w-3 h-3 inline mr-1" />
                      )}
                      {t('accountDetails.rib.upload' as any)}
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handleRibUpload}
                        className="hidden"
                        disabled={ribUploading}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Balance Chart - for regular accounts (not crypto, pea_stock, or static assets) */}
          {account.subtype !== 'cash' &&
            account.subtype !== 'real_estate' &&
            account.subtype !== 'other_asset' &&
            account.subtype !== 'watch' &&
            account.subtype !== 'jewelry' &&
            account.subtype !== 'precious_metal' &&
            account.subtype !== 'crypto' &&
            account.subtype !== 'pea_stock' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('accountDetails.balanceHistory' as any)}
                  </h3>
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                    {Object.keys(BALANCE_PERIOD_DAYS).map((key) => (
                      <button
                        key={key}
                        onClick={() => setBalanceChartPeriod(key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          balanceChartPeriod === key
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        {t(`accounts.period.${key}` as any)}
                      </button>
                    ))}
                  </div>
                </div>
                {balanceChartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={balanceChartData}>
                      <defs>
                        <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#16c47f" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#16c47f" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        tickFormatter={(d: string) => {
                          const date = new Date(d + 'T12:00:00Z');
                          const days = BALANCE_PERIOD_DAYS[balanceChartPeriod] || 30;
                          if (days <= 30)
                            return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                              day: 'numeric',
                              month: 'short',
                            });
                          return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                            month: 'short',
                            year: days > 365 ? '2-digit' : undefined,
                          });
                        }}
                        interval="preserveStartEnd"
                        minTickGap={40}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        tickFormatter={(v: number) => formatCompact(v)}
                        width={70}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.[0]) return null;
                          const date = new Date(label + 'T12:00:00Z');
                          const dateStr = date.toLocaleDateString(
                            language === 'fr' ? 'fr-FR' : 'en-US',
                            {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            }
                          );
                          return (
                            <div
                              style={{
                                background: 'rgba(17, 24, 39, 0.95)',
                                backdropFilter: 'blur(12px)',
                                borderRadius: '14px',
                                padding: '14px 18px',
                                boxShadow:
                                  '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
                                minWidth: '160px',
                              }}
                            >
                              <div
                                style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px' }}
                              >
                                {dateStr}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                  style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: '#16c47f',
                                    boxShadow: '0 0 8px #16c47f66',
                                    flexShrink: 0,
                                  }}
                                />
                                <span style={{ color: '#e5e7eb', fontSize: '12px' }}>
                                  {t('accounts.modal.balance' as any)}
                                </span>
                              </div>
                              <div
                                style={{
                                  color: '#fff',
                                  fontSize: '18px',
                                  fontWeight: 700,
                                  letterSpacing: '-0.02em',
                                  marginTop: '4px',
                                  paddingLeft: '18px',
                                }}
                              >
                                {formatAccountAmount(payload[0].value as number)}
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#16c47f"
                        strokeWidth={2}
                        fill="url(#balanceGradient)"
                        dot={false}
                        activeDot={{ r: 5, fill: '#16c47f', strokeWidth: 0 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center">
                    <div className="text-center">
                      <BarChart3 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 dark:text-gray-500 text-sm">
                        {t('accountDetails.noTransactions')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Actions & Filters - hide for static asset types */}
          {account.subtype !== 'cash' &&
            account.subtype !== 'real_estate' &&
            account.subtype !== 'other_asset' &&
            account.subtype !== 'watch' &&
            account.subtype !== 'jewelry' &&
            account.subtype !== 'precious_metal' &&
            account.subtype !== 'crypto' &&
            account.subtype !== 'pea_stock' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
                {/* Top row: Search + Actions */}
                <div className="flex flex-col lg:flex-row gap-3 justify-between">
                  {/* Search */}
                  <div className="flex-1 relative max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder={t('transactions.search')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all"
                    />
                  </div>

                  {/* Actions - right side */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsTransferModalOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold rounded-xl text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <ArrowLeftRight className="w-4 h-4" />
                      {t('transactions.transfer')}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTransaction(undefined);
                        setIsTransactionModalOpen(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                      {t('transactions.addNew')}
                    </button>
                  </div>
                </div>

                {/* Bottom row: Type filters + Date range */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {/* Type Filters */}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setFilter('all')}
                      className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
                        filter === 'all'
                          ? 'bg-[#16c47f] text-white font-semibold shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('transactions.filter.all')}
                    </button>
                    <button
                      onClick={() => setFilter('income')}
                      className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
                        filter === 'income'
                          ? 'bg-green-500 text-white font-semibold shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('transactions.filter.income')}
                    </button>
                    <button
                      onClick={() => setFilter('expense')}
                      className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
                        filter === 'expense'
                          ? 'bg-[#78909C] text-white font-semibold shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('transactions.filter.expense')}
                    </button>
                    <button
                      onClick={() => setFilter('transfer')}
                      className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
                        filter === 'transfer'
                          ? 'bg-blue-500 text-white font-semibold shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('transactions.filter.transfer')}
                    </button>
                  </div>

                  <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700" />

                  {/* Date Range */}
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <DatePicker
                        selected={dateRange.startDate}
                        onChange={(date) =>
                          setDateRange({
                            ...dateRange,
                            startDate: date,
                          })
                        }
                        placeholderText={t('transactions.filter.from')}
                        dateFormat="dd/MM/yyyy"
                        className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] text-xs"
                        wrapperClassName="w-full"
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                    </div>
                    <span className="text-gray-400 dark:text-gray-500 text-xs">→</span>
                    <div className="relative">
                      <DatePicker
                        selected={dateRange.endDate}
                        onChange={(date) =>
                          setDateRange({
                            ...dateRange,
                            endDate: date,
                          })
                        }
                        placeholderText={t('transactions.filter.to')}
                        dateFormat="dd/MM/yyyy"
                        className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] text-xs"
                        wrapperClassName="w-full"
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                    </div>
                    {(dateRange.startDate || dateRange.endDate) && (
                      <button
                        onClick={clearDateRange}
                        className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors"
                        title={t('transactions.filter.clearDates')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* KPI Summary - period-filtered, hide for static asset types */}
          {account.subtype !== 'cash' &&
            account.subtype !== 'real_estate' &&
            account.subtype !== 'other_asset' &&
            account.subtype !== 'watch' &&
            account.subtype !== 'jewelry' &&
            account.subtype !== 'precious_metal' &&
            account.subtype !== 'crypto' &&
            account.subtype !== 'pea_stock' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('accountDetails.stats.income')}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatAccountAmount(periodStats.totalIncome)}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-[#78909C]/10 flex items-center justify-center">
                      <TrendingDown className="w-3.5 h-3.5 text-[#78909C]" />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('accountDetails.stats.expense')}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatAccountAmount(periodStats.totalExpense)}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <ArrowDownLeft className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('accountDetails.stats.transfersIn')}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatAccountAmount(periodStats.totalTransfersIn)}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <ArrowUpRight className="w-3.5 h-3.5 text-indigo-500" />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('accountDetails.stats.transfersOut')}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatAccountAmount(periodStats.totalTransfersOut)}
                  </p>
                </div>
              </div>
            )}

          {/* Transaction List - hide for static asset types */}
          {account.subtype !== 'cash' &&
            account.subtype !== 'real_estate' &&
            account.subtype !== 'other_asset' &&
            account.subtype !== 'watch' &&
            account.subtype !== 'jewelry' &&
            account.subtype !== 'precious_metal' &&
            account.subtype !== 'crypto' &&
            account.subtype !== 'pea_stock' && (
              <div>
                <div className="px-1 py-3">
                  <h2 className="font-semibold text-gray-900 dark:text-white text-sm">
                    {t('accountDetails.transactions')} ({filteredTransactions.length})
                  </h2>
                </div>

                {loading ? (
                  <div className="p-12 text-center">
                    <div className="inline-block w-10 h-10 border-4 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 text-center py-12">
                    <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 mb-1">
                      {t('accountDetails.noTransactions')}
                    </p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm">
                      {t('accountDetails.noTransactionsDesc')}
                    </p>
                  </div>
                ) : (
                  <GroupedTransactionList
                    transactions={filteredTransactions}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    showAccount={true}
                    highlightAccountId={accountId}
                    language={language}
                  />
                )}
              </div>
            )}
        </div>

        <TransactionModal
          isOpen={isTransactionModalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          transaction={selectedTransaction}
          defaultAccountId={accountId}
          transactionHistory={allTransactions}
        />

        <TransferModal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          onSuccess={handleModalSuccess}
          defaultFromAccountId={accountId}
        />

        {/* RIB PDF Viewer Modal */}
        <AnimatePresence>
          {ribViewerOpen && ribBlobUrl && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
              onClick={closeRibViewer}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="relative w-full max-w-4xl h-[85vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700/50">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('accountDetails.rib.fileName' as any)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleRibDownload}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-[#16c47f] transition-colors"
                      title={t('accountDetails.rib.download' as any)}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      onClick={closeRibViewer}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                {/* PDF iframe */}
                <iframe
                  src={ribBlobUrl}
                  className="w-full h-[calc(100%-56px)] bg-gray-100 dark:bg-gray-900"
                  title="RIB PDF"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
